import React, { useState, useEffect } from 'react';
import { getActivity, getSyncStatus } from '../api/apiService';

const CommitActivity = () => {
  const [activities, setActivities] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setYears(Array.from({ length: 5 }, (_, i) => currentYear - i));
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [selectedYear]);

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await getActivity(selectedYear);
      const data = response.data.data;
      
      const activityMap = {};
      data.forEach(item => {
        activityMap[item.date] = item.count;
      });
      setActivities(activityMap);
    } catch (error) {
      console.error("Failed to fetch activity", error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await getSyncStatus();
      setSyncStatus(response.data.data);
    } catch (error) {
      console.error("Failed to fetch sync status", error);
    }
  };

  const getDaysInYear = (year) => {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    const days = [];
    for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const getColor = (count) => {
    if (!count) return 'bg-black/60 border border-cyan-900/30';
    if (count < 3) return 'bg-cyan-900 shadow-[0_0_5px_rgba(0,255,255,0.2)] border border-cyan-700';
    if (count < 6) return 'bg-cyan-600 shadow-[0_0_8px_rgba(0,255,255,0.4)] border border-cyan-400';
    if (count < 10) return 'bg-cyan-400 shadow-[0_0_12px_rgba(0,255,255,0.6)] border border-white';
    return 'bg-fuchsia-500 shadow-[0_0_15px_rgba(255,0,255,0.8)] border border-white';
  };

  const days = getDaysInYear(selectedYear);

  // Group days by week (Sunday to Saturday)
  const weeks = [];
  let currentWeek = [];
  
  // Pad the first week with empty slots if the year doesn't start on Sunday
  const firstDayOfWeek = days[0].getDay();
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push(null);
  }

  days.forEach(day => {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  if (currentWeek.length > 0) {
    // Pad the last week with empty slots
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return (
    <div className="cyber-card p-6 overflow-hidden">
      <div className="flex items-center justify-between border-b border-cyan-400/20 pb-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-cyan-400 bg-cyan-900/30 flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%)' }}>
            <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <h3 className="font-black text-sm text-cyan-400 uppercase tracking-widest drop-shadow-[0_0_5px_rgba(0,255,255,0.4)]">> COMMIT_MATRIX</h3>
            <p className="cyber-label text-[9px] mt-0.5">Tracking code injection events</p>
          </div>
        </div>
        
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(e.target.value)}
          className="cyber-input px-3 py-1.5 text-xs focus:outline-none cursor-pointer uppercase font-bold"
        >
          {years.map(y => (
            <option key={y} value={y} className="bg-[#030305] text-cyan-400">{y}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto pb-4 custom-scrollbar">
        <div className="inline-flex gap-[3px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-[3px]">
              {week.map((day, dayIndex) => {
                if (!day) return <div key={dayIndex} className="w-[11px] h-[11px]" />;
                
                // Format date to match API response (YYYY-MM-DD)
                const dateString = day.toISOString().split('T')[0];
                const count = activities[dateString] || 0;
                
                return (
                  <div 
                    key={dayIndex} 
                    className={`w-[11px] h-[11px] ${getColor(count)}`}
                    title={`${dateString}: ${count} commits`}
                    style={{ clipPath: 'polygon(2px 0, 100% 0, 100% calc(100% - 2px), calc(100% - 2px) 100%, 0 100%, 0 2px)' }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-4 flex items-center justify-between text-xs text-white/50">
        <div className="flex flex-col gap-1">
          <div className="flex gap-4">
            <span className="cyber-label text-[9px]">DIAGNOSTICS_DATA</span>
            {syncStatus && (
              <span className="flex items-center gap-1.5 border-l border-cyan-400/20 pl-4">
                <span className="cyber-label text-[9px]">LAST_SYNC:</span>
                <span className="text-cyan-400 font-mono">
                  {syncStatus.lastSyncDate ? new Date(syncStatus.lastSyncDate).toLocaleString() : 'PENDING'}
                </span>
                <span className={`w-1.5 h-1.5 ml-1 ${syncStatus.status === 'success' ? 'bg-green-500 shadow-[0_0_5px_#0f0]' : syncStatus.status === 'in_progress' ? 'bg-cyan-500 animate-pulse shadow-[0_0_5px_#0ff]' : 'bg-red-500 shadow-[0_0_5px_#f00]'}`} />
              </span>
            )}
          </div>
          {syncStatus && syncStatus.repositories && syncStatus.repositories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="cyber-label text-[9px] mr-1">TRACKED_NODES:</span>
              {syncStatus.repositories.map(repo => (
                <span key={repo} className="text-[9px] font-mono border border-fuchsia-500/30 text-fuchsia-400 bg-fuchsia-900/20 px-1 py-0.5">
                  {repo}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="cyber-label text-[9px]">IDLE</span>
          <div className="flex gap-[3px]">
            {[0, 2, 5, 8, 12].map((count, i) => (
              <div 
                key={i} 
                className={`w-[11px] h-[11px] ${getColor(count)}`} 
                style={{ clipPath: 'polygon(2px 0, 100% 0, 100% calc(100% - 2px), calc(100% - 2px) 100%, 0 100%, 0 2px)' }}
              />
            ))}
          </div>
          <span className="cyber-label text-[9px]">MAX</span>
        </div>
      </div>
    </div>
  );
};

export default CommitActivity;
